import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from './api';

const fmt = (value, digits = 2) => {
  if (value === '' || value == null || !Number.isFinite(Number(value))) return '—';
  return Number(value).toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const VectorBTTransactionsPage = ({ onSelectSymbol }) => {
  const [trades, setTrades] = useState([]);
  const [summary, setSummary] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    apiFetch('/api/vectorbt/transactions')
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'Unable to load VectorBT transactions');
        setTrades(body.trades || []);
        setSummary(body.summary || []);
      })
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const visible = useMemo(() => {
    const needle = query.trim().toUpperCase();
    return needle ? trades.filter((trade) => String(trade.symbol).toUpperCase().includes(needle)) : trades;
  }, [trades, query]);

  const totalPnl = trades.reduce((total, trade) => total + (Number(trade.pnl) || 0), 0);
  const winners = trades.filter((trade) => Number(trade.pnl) > 0).length;

  return (
    <main className="vectorbt-page">
      <div className="vectorbt-heading">
        <div>
          <h1>VectorBT Transactions</h1>
          <p>Historical trades generated by the arch/base breakout strategy.</p>
        </div>
        <div className="vectorbt-actions">
          <button type="button" onClick={load} disabled={loading}>Refresh</button>
          <a href="/api/vectorbt/transactions" target="_blank" rel="noreferrer">Raw JSON</a>
        </div>
      </div>

      <div className="vectorbt-cards">
        <div><small>Transactions</small><strong>{trades.length}</strong></div>
        <div><small>Winners</small><strong>{winners}</strong></div>
        <div><small>Win rate</small><strong>{trades.length ? `${fmt((winners / trades.length) * 100, 1)}%` : '—'}</strong></div>
        <div><small>Total P&amp;L</small><strong className={totalPnl >= 0 ? 'positive' : 'negative'}>{fmt(totalPnl)}</strong></div>
        <div><small>Symbols in summary</small><strong>{summary.length}</strong></div>
      </div>

      <div className="vectorbt-toolbar">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter symbol…" aria-label="Filter symbol" />
        <span>{visible.length} shown</span>
      </div>

      {loading && <div className="table-message">Loading VectorBT transactions…</div>}
      {!loading && error && <div className="table-message error">{error}</div>}
      {!loading && !error && visible.length === 0 && <div className="table-message">No backdated transactions found. Run the VectorBT backtest first.</div>}
      {!loading && !error && visible.length > 0 && (
        <div className="vectorbt-table-wrap">
          <table className="results-table vectorbt-table">
            <thead><tr><th>Symbol</th><th>Entry date</th><th className="number">Entry</th><th>Exit date</th><th className="number">Exit</th><th className="number">P&amp;L</th><th className="number">Return</th><th /></tr></thead>
            <tbody>{visible.map((trade) => (
              <tr key={trade.id}>
                <td className="symbol">{trade.symbol}</td>
                <td>{String(trade.entry_date).slice(0, 10) || '—'}</td>
                <td className="number">{fmt(trade.entry_price)}</td>
                <td>{String(trade.exit_date).slice(0, 10) || 'Open'}</td>
                <td className="number">{fmt(trade.exit_price)}</td>
                <td className={`number ${Number(trade.pnl) >= 0 ? 'positive' : 'negative'}`}>{fmt(trade.pnl)}</td>
                <td className={`number ${Number(trade.return_pct) >= 0 ? 'positive' : 'negative'}`}>{trade.return_pct === '' ? '—' : `${fmt(trade.return_pct)}%`}</td>
                <td><button type="button" className="vectorbt-chart-button" onClick={() => onSelectSymbol(trade)}>Chart</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </main>
  );
};

export default VectorBTTransactionsPage;
